// Вход в шапке: кнопка «Войти», меню пользователя, плашка гостя.
//
// Без входа (AUTH_MODE=disabled) ничего из этого не показывается. Гостю
// (вход включён, но не выполнен) класс html.auth-anonymous прячет всё, что
// создаёт и меняет дашборды (.requires-sign-in), — бэкенд всё равно ответил
// бы 401, а интерфейс не должен предлагать недоступное.

import { auth, isAnonymous, isSignedIn, onAuthChange, primaryRole, signIn, signOut, userDisplayName } from '../auth';
import { tr } from '../ui-i18n';

import { controls } from './controls';
import { refreshIcons } from './dom';
import { currentDashboardKey, loadDashboard, refreshDashboardList, renderDashboardManager } from './dashboards';
import { setStatus } from './status';
import { setViewMode } from './workspace';
import { state } from './state';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Администратор',
  editor: 'Сотрудник',
  viewer: 'Клиент',
  api: 'API',
};

export function roleLabel(role: string | null): string {
  if (!role) return '';
  const russian = ROLE_LABELS[role];
  return russian ? tr(russian) : role;
}

function bannerText(): string {
  const { lostReason } = auth();
  if (lostReason === 'expired') return tr('Сеанс входа истёк. Войдите снова, чтобы продолжить работу со своими дашбордами.');
  if (lostReason === 'rejected') return tr('Сервер не принял вход. Попробуйте войти снова или обратитесь к администратору.');
  return tr('Войдите, чтобы создавать свои дашборды');
}

export function applyAuthUi(): void {
  const root = document.documentElement;
  const anonymous = isAnonymous();
  const signedIn = isSignedIn();
  root.classList.toggle('auth-anonymous', anonymous);
  root.classList.toggle('auth-signed-in', signedIn);
  controls.authArea.hidden = !anonymous && !signedIn;
  controls.authSignIn.hidden = !anonymous;
  controls.authUserButton.parentElement!.hidden = !signedIn;
  controls.authBanner.hidden = !anonymous;
  if (anonymous) controls.authBannerText.textContent = bannerText();
  if (signedIn) {
    const name = userDisplayName();
    const me = auth().me;
    controls.authUserName.textContent = name;
    controls.authUserMenuName.textContent = name;
    controls.authUserMenuEmail.textContent = me?.email ?? '';
    controls.authUserMenuRole.textContent = roleLabel(primaryRole());
    controls.authUserMenuRole.hidden = !primaryRole();
    controls.authUserButton.setAttribute('aria-label', tr('Пользователь: {0}', name));
  } else {
    setUserMenuOpen(false);
  }
}

function setUserMenuOpen(open: boolean): void {
  controls.authUserMenu.hidden = !open;
  controls.authUserButton.setAttribute('aria-expanded', String(open));
}

// Переход ко входу начат: повторные нажатия не запускают второй, кнопки
// показывают ожидание. Сбрасывается ошибкой, таймером (переход не случился)
// и возвратом на страницу из кэша браузера (pageshow persisted).
let signingIn = false;
let signingInTimer: number | undefined;
const SIGN_IN_STALL_MS = 15_000;

function setSigningIn(active: boolean): void {
  signingIn = active;
  window.clearTimeout(signingInTimer);
  [controls.authSignIn, controls.authBannerSignIn].forEach((button) => {
    button.disabled = active;
    button.setAttribute('aria-busy', String(active));
  });
  if (active) {
    controls.authBannerText.textContent = tr('Переход ко входу…');
    signingInTimer = window.setTimeout(() => {
      setSigningIn(false);
      if (isAnonymous()) controls.authBannerText.textContent = bannerText();
    }, SIGN_IN_STALL_MS);
  }
}

async function startSignIn(): Promise<void> {
  if (signingIn) return;
  // Гость сохранить ничего не может — спрашивать о несохранённом незачем.
  if (!isAnonymous() && state.dirty && !window.confirm(tr('Несохранённые изменения будут потеряны. Перейти ко входу?'))) return;
  setSigningIn(true);
  try {
    await signIn();
  } catch (error) {
    setSigningIn(false);
    const message = tr('Не удалось начать вход: {0}', error instanceof Error ? error.message : String(error));
    // В плашке, рядом с кнопкой: строка статуса внизу уже занята прежней ошибкой.
    controls.authBannerText.textContent = `${message}. ${tr('Попробуйте ещё раз.')}`;
    setStatus(message, 'error');
  }
}

/**
 * Вход потерян посреди работы (refresh_token истёк или отозван): гостевой
 * режим без перезагрузки — список только общих, текущий дашборд, если он
 * общий, остаётся открытым на просмотр.
 */
async function enterAnonymousMode(): Promise<void> {
  applyAuthUi();
  setViewMode(true);
  try {
    await refreshDashboardList();
    const current = currentDashboardKey();
    const next = current && state.dashboards.some((item) => item.id === current) ? current : state.dashboards[0]?.id;
    if (next) await loadDashboard(next, true);
    if (controls.dashboardManagerDialog.open) renderDashboardManager();
  } catch {}
  setStatus(bannerText(), 'error');
}

export function bindAuthEvents(): void {
  refreshIcons(controls.authArea);
  refreshIcons(controls.authBanner);
  controls.authSignIn.addEventListener('click', () => void startSignIn());
  controls.authBannerSignIn.addEventListener('click', () => void startSignIn());
  window.addEventListener('pageshow', (event) => {
    if (event.persisted && signingIn) {
      setSigningIn(false);
      applyAuthUi();
    }
  });
  controls.authUserButton.addEventListener('click', () => setUserMenuOpen(controls.authUserMenu.hidden));
  controls.authSignOut.addEventListener('click', () => {
    if (state.dirty && !window.confirm(tr('Несохранённые изменения будут потеряны. Выйти?'))) return;
    setUserMenuOpen(false);
    void signOut();
  });
  document.addEventListener('click', (event) => {
    if (!controls.authUserMenu.hidden && !(event.target as Element).closest('.auth-user-shell')) setUserMenuOpen(false);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !controls.authUserMenu.hidden) {
      setUserMenuOpen(false);
      controls.authUserButton.focus();
    }
  });
  onAuthChange(() => {
    if (isAnonymous()) void enterAnonymousMode();
    else applyAuthUi();
  });
}
